import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { AcquireOwnSharesPowerOfAttorneyForm } from "./acquireOwnSharesPowerOfAttorneyDomain";
import {
  LocalizedMarkdownQuestion,
  NumberField,
  NumberQuestion,
  YesNoQuestion,
} from "../common/commonComponents";
import { Box, VBox } from "../../components-v2/Box";
import { CURRENCY_NOK } from "../../utils/currency";

import { useTranslation } from "../../stores/RootStoreContext";
import { RadioFieldQuestion } from "../../common/commonComponents";
import { Alert } from "../../components-v2/Alert";

export const AcquireOwnSharesPowerOfAttorneyView: FC<{
  form: AcquireOwnSharesPowerOfAttorneyForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();

  useEffect(() => form.init(), [form]);

  useEffect(() => {
    if (form.otherProperties) {
      form.otherProperties.map((data) => {
        if (!data.model.validatedValue) {
          const text = t("decisions.acquireOwnSharesPowerOfAttorney.otherProperties.text", {
            lang: data.language,
          });
          data.model.setAndCommitInputValue(text);
        }
      });
    }
  }, [form, t]);

  return (
    <VBox gap={"lg"}>
      {form.maximumShareCapitalValue <= 0 && (
        <Alert message={t("decisions.acquireOwnSharesPowerOfAttorney.error")} type={"error"} />
      )}
      {form.maximumShareCapitalValue > 0 && (
        <>
          <RadioFieldQuestion
            row
            question={{
              key: "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.title",
            }}
            infoText={{
              key: "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.helperText",
            }}
            field={form.calculateShareCapitalFromShares}
            fieldName={"powerOfAttorneyFor"}
            isLocked={form.isProjectLocked}
          />
          {form.isCalculateShareCapitalSelected && (
            <>
              {form.showShareCapitalValue && (
                <>
                  <NumberField
                    field={form.shareCapitalValue}
                    prefix={CURRENCY_NOK}
                    isLocked={form.isProjectLocked}
                    inline
                  />
                  <Alert
                    message={t(
                      "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.from-total.info",
                      {
                        numberOfShares: {
                          type: "number",
                          value: form.totalSharesValue.parsedValue ?? 0,
                        },
                        nominalValue: { type: "number", value: form.nominalValue },
                        maximumShareCapital: {
                          type: "number",
                          value: form.maximumShareCapitalValue,
                        },
                      },
                    )}
                    type={"info"}
                  />
                </>
              )}
              {!form.showShareCapitalValue && (
                <>
                  <NumberField
                    field={form.totalSharesValue}
                    isLocked={form.isProjectLocked}
                    inline
                  />
                  <Alert
                    message={t(
                      "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.from-shares.info",
                      {
                        shareCapital: {
                          type: "number",
                          value: form.shareCapitalValue.parsedValue ?? 0,
                        },
                        maximumShareCapital: {
                          type: "number",
                          value: form.maximumShareCapitalValue,
                        },
                      },
                    )}
                    type={"info"}
                  />
                </>
              )}
              <Box wrap>
                <NumberQuestion
                  question={t("decisions.acquireOwnSharesPowerOfAttorney.minimum-price.title")}
                  field={form.minimumSharePrice}
                  prefix={CURRENCY_NOK}
                  isLocked={form.isProjectLocked}
                  inline
                />
                <NumberQuestion
                  question={t("decisions.acquireOwnSharesPowerOfAttorney.maximum-price.title")}
                  field={form.maximumSharePrice}
                  prefix={CURRENCY_NOK}
                  isLocked={form.isProjectLocked}
                  inline
                />
              </Box>
              <YesNoQuestion
                question={t(
                  "decisions.acquireOwnSharesPowerOfAttorney.isProportionallyAdjusted.title",
                )}
                field={form.isProportionallyAdjusted}
              />

              <LocalizedMarkdownQuestion
                name={"otherProperties"}
                question={t("decisions.acquireOwnSharesPowerOfAttorney.otherProperties.title")}
                textModels={form.otherProperties}
                isLocked={form.isProjectLocked}
              />
              <RadioFieldQuestion
                question={{
                  key: "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalDuration.questionText",
                }}
                field={form.shareCapitalDuration}
                fieldName={"powerOfAttorneyFor"}
                isLocked={form.isProjectLocked}
              />
              {form.isDurationMonths && (
                <NumberQuestion
                  question={t(
                    "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalDurationMonths.questionText",
                  )}
                  field={form.shareCapitalDurationMonths}
                  onBlur={() => form.setDurationMonthsLimit()}
                  infoText={t(
                    "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalDurationMonths.helperText",
                  )}
                  isLocked={form.isProjectLocked}
                  inline
                />
              )}
            </>
          )}
        </>
      )}
    </VBox>
  );
});
